import { css, keyframes } from "@emotion/react";
import styled from "@emotion/styled";

import { FONT_WEIGHT_SEMI_BOLD } from "@skilitics/designer-ui-components";

export const cardGap = 25;

export const CardContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  aspect-ratio: 4 / 3;
  flex: 1 1 calc(33% - ${cardGap}px);
  max-width: calc(33% - ${cardGap / 2}px);

  background-color: ${({ theme }) => theme.surface};
  border-radius: 16px;
  box-shadow: 0px 3px 7px 0px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  user-select: none;

  @media screen and (min-width: 1200px) {
    flex: 1 1 calc(25% - ${cardGap}px);
    max-width: calc(25% - ${cardGap - 5}px);
  }
  @media screen and (min-width: 1440px) {
    flex: 1 1 calc(20% - ${cardGap}px);
    max-width: calc(20% - ${cardGap - 4}px);
  }
  @media screen and (min-width: 1800px) {
    flex: 1 1 calc(16.666% - ${cardGap}px);
    max-width: calc(16.666% - ${cardGap - 3}px);
  }
`;

export const Thumbnail = styled.div`
  position: relative;
  z-index: 1;

  display: flex;
  width: 100%;
  flex: 1;
  min-height: 0;

  overflow: hidden;

  border-start-start-radius: 16px;
  border-start-end-radius: 16px;

  > img {
    width: 100%;
    height: 100%;

    object-fit: cover;
  }
`;

export const DetailsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 5px;

  width: 100%;
  height: auto;

  font-size: 12px;

  position: absolute;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 10;

  padding: 15px 5px 12px 15px;

  color: ${({ theme }) => theme.onNeutralContainer};
`;

// Darkens the bottom of the card for text legibility; slides up on card hover to deepen.
export const GradientOverlay = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 80px;
  z-index: 2;

  pointer-events: none;

  background: ${({ theme }) => `linear-gradient(180deg, rgba(0, 0, 0, 0) 10%, ${theme.alwaysBlack} 90%)`};

  transform: translateY(40px);
  transition: transform 250ms ease-in-out;

  @supports (-webkit-backdrop-filter: none) or (backdrop-filter: none) {
    backdrop-filter: blur(1px);
  }
`;

export const Title = styled.div`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;

  font-weight: ${FONT_WEIGHT_SEMI_BOLD};

  overflow: hidden;
`;

const glow = keyframes`
  from { 
    opacity: 1;
  }
  
  to {
    opacity: 0.15;
  }
`;

// Positions the icons or opened-by user avatar in the card
export const IconContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 4;

  display: grid;
  padding: 10px;

  & > * {
    grid-area: 1 / 1;
  }
`;

// Wrapper for the lock/trash icon handling animation and backdrop
// We want pointer-events: none so the tooltip on the user avatar behind this works.
export const IconWrapper = styled.div<{ $show: boolean; $busy?: boolean }>`
  pointer-events: none;
  padding: 8px;
  color: ${({ theme }) => theme.generalTextIconFill};

  opacity: ${({ $show }) => ($show ? 1 : 0)};

  transition: opacity 300ms ease-in-out;

  background-color: rgba(0, 0, 0, 0.1);
  border-radius: 50px;

  @supports (-webkit-backdrop-filter: none) or (backdrop-filter: none) {
    backdrop-filter: blur(8px);
  }

  ${({ $busy = false }) =>
    $busy &&
    css`
      animation: ${glow} 750ms ease-in-out infinite alternate;
    `};
`;
